/**
 * Named endpoint registry — THE place to reconfigure the backend contract.
 *
 * Every call the UI makes goes through a name in this table. When the backend
 * rewrite moves or renames a route, change it here and nowhere else. Paths use
 * `:param` placeholders filled by `resolve()`. The base URL comes from
 * `VITE_API_BASE` (empty = same origin, which the dev server proxies to the
 * Compose API).
 *
 * The table is assembled from one file per domain under `routes/` so that
 * domains can be edited independently; every entry still has the shape
 * `{ method, path, note? }`, so an OpenAPI document can generate the whole
 * registry later without touching callers.
 */
import type { EndpointDef, HttpMethod } from './endpointTypes'
import { billingRoutes } from './routes/billing'
import { coreRoutes } from './routes/core'
import { dealModelRoutes } from './routes/dealModels'
import { diligenceRoutes } from './routes/diligence'
import { legalRoutes } from './routes/legal'
import { propertyRoutes } from './routes/property'
import { registerRoutes } from './routes/register'

export type { EndpointDef, HttpMethod } from './endpointTypes'

export const API_BASE: string = (import.meta.env.VITE_API_BASE as string | undefined) ?? ''

export const endpoints = {
  ...coreRoutes,
  ...registerRoutes,
  ...propertyRoutes,
  ...diligenceRoutes,
  ...legalRoutes,
  ...dealModelRoutes,
  ...billingRoutes,
} as const satisfies Record<string, EndpointDef>

export type EndpointName = keyof typeof endpoints

export type PathParams = Record<string, string | number>
export type QueryParams = Record<string, string | number | boolean | null | undefined>

/** Build the full URL for a named endpoint. */
export function resolve(name: EndpointName, params?: PathParams, query?: QueryParams): string {
  const def: EndpointDef = endpoints[name]
  let path = def.path.replace(/:([A-Za-z_]+)/g, (_match, key: string) => {
    const value = params?.[key]
    if (value === undefined) throw new Error(`Endpoint ${name} needs path parameter :${key}`)
    return encodeURIComponent(String(value))
  })
  if (query) {
    const search = new URLSearchParams()
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue
      search.set(key, String(value))
    }
    const encoded = search.toString()
    if (encoded) path += `?${encoded}`
  }
  return `${API_BASE}${path}`
}

export function methodOf(name: EndpointName): HttpMethod {
  return (endpoints[name] as EndpointDef).method
}
